import { api } from './api';

export type ShowcaseStatus = 'generating' | 'draft' | 'published';
export type ShowcaseLinkType = 'repo' | 'video' | 'doc' | 'other';

export interface ShowcaseLink {
  url: string;
  label: string;
  type: ShowcaseLinkType;
}

export interface ShowcaseEntry {
  showcaseId: string;
  sessionId: string;
  status: ShowcaseStatus;
  challengeTitle?: string;
  challengeSummary?: string;
  industry?: string;
  tags?: string[];
  learnings?: string[];
  assets?: ShowcaseLink[];
  requestId?: string;
  generatedAt?: string;
  publishedAt?: string;
  publishedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ShowcaseStats {
  totalSessions: number;
  sessionsLast30Days: number;
  totalPublished: number;
  distinctIndustries: number;
}

export const showcaseService = {
  async list(): Promise<{ entries: ShowcaseEntry[]; stats: ShowcaseStats }> {
    const res = await api.get('/api/showcase');
    return res.data;
  },

  async getStats(): Promise<ShowcaseStats> {
    const res = await api.get('/api/showcase/stats');
    return res.data.stats;
  },

  async get(showcaseId: string): Promise<ShowcaseEntry> {
    const res = await api.get(`/api/showcase/${showcaseId}`);
    return res.data.entry;
  },

  async enable(sessionId: string, links: ShowcaseLink[] = []): Promise<ShowcaseEntry> {
    const res = await api.post(`/api/sessions/${sessionId}/showcase`, { links });
    return res.data.entry;
  },

  async disable(sessionId: string): Promise<void> {
    await api.delete(`/api/sessions/${sessionId}/showcase`);
  },

  async updateLinks(sessionId: string, links: ShowcaseLink[]): Promise<void> {
    await api.put(`/api/sessions/${sessionId}/showcase/links`, { links });
  },

  async generate(sessionId: string): Promise<{ showcaseId: string; requestId: string }> {
    const res = await api.post(`/api/sessions/${sessionId}/showcase/generate`);
    return res.data;
  },

  async update(showcaseId: string, fields: Partial<ShowcaseEntry>): Promise<ShowcaseEntry> {
    const res = await api.patch(`/api/showcase/${showcaseId}`, fields);
    return res.data.entry;
  },

  async publish(showcaseId: string): Promise<ShowcaseEntry> {
    const res = await api.patch(`/api/showcase/${showcaseId}`, { status: 'published' });
    return res.data.entry;
  },

  async deleteEntry(showcaseId: string): Promise<void> {
    await api.delete(`/api/showcase/${showcaseId}`);
  },
};
